import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { TicketIcon, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  clearPostSignupAthleteCodePrompt,
  hasPendingPostSignupAthleteCodePrompt,
} from "@/lib/post-signup-athlete-code";

export function usePostSignupAthleteCode() {
  const location = useLocation();
  const { isLoaded, isSignedIn } = useAuth();
  const { user, isLoading } = useCurrentUser();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || isLoading || !user) return;

    if (location.pathname.startsWith("/auth/")) {
      setShow(false);
      return;
    }

    if (!hasPendingPostSignupAthleteCodePrompt()) {
      setShow(false);
      return;
    }

    if (user.promoCodeUsed || user.athleteId) {
      clearPostSignupAthleteCodePrompt();
      setShow(false);
      return;
    }

    setShow(true);
  }, [isLoaded, isSignedIn, isLoading, location.pathname, user]);

  return show;
}

export const PostSignupAthleteCodeBanner = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const hidePrompt = () => {
    clearPostSignupAthleteCodePrompt();
    setDismissed(true);
  };

  const handleDismiss = () => {
    hidePrompt();
  };

  const handleGoToProfile = () => {
    hidePrompt();
    navigate("/profile#/promo-code");
  };

  const handleSubmit = async () => {
    const trimmedCode = code.trim();
    if (!trimmedCode) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await getToken();
      await api.post<{ success: boolean; linked: boolean; reason?: string }>(
        "/api/auth/promo-code",
        { code: trimmedCode },
        token
      );

      clearPostSignupAthleteCodePrompt();
      setCode("");
      setSuccess(true);
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Invalid code. Try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (dismissed) {
    return null;
  }

  if (success) {
    return (
      <div className="relative w-full bg-emerald-600 text-white">
        <div className="flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-medium">
          <TicketIcon className="size-4" />
          Code linked successfully!
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full bg-tenant-primary text-white">
      <div className="flex flex-col gap-2 py-2.5 px-4 md:flex-row md:items-center md:justify-between md:gap-4 md:px-32">
        <div className="flex items-center gap-2 text-sm font-medium">
          <TicketIcon className="size-4 shrink-0" />
          <span>Have a referral code? Link it to your account.</span>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setError(null);
            }}
            placeholder="Enter code"
            className="h-8 w-40 rounded-md bg-white/90 px-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
            autoComplete="off"
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleSubmit();
            }}
          />
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || !code.trim()}
            className="h-8 rounded-md bg-white/20 px-3 text-sm font-semibold transition-colors hover:bg-white/30 disabled:opacity-50"
          >
            {isSubmitting ? "..." : "Link"}
          </button>
          <button
            type="button"
            onClick={handleGoToProfile}
            className="hidden h-8 rounded-md px-2 text-xs text-white/70 transition-colors hover:text-white md:block"
          >
            Add later
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="ml-1 rounded p-0.5 text-white/60 transition-colors hover:text-white"
            aria-label="Dismiss"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/90 px-4 py-1 text-center text-xs font-medium md:px-32">
          {error}
        </div>
      )}
    </div>
  );
};
